<template>
  <v-col class="pa-6">
    <div v-if="!creatingArticle && !viewingRequest">
      <v-row>
        <v-col cols="12" md="3">
          <v-text-field
            class="input ma-0 pa-0"
            label="Search..."
            prepend-inner-icon="mdi-magnify"
            v-model="search"
            rounded
            :hide-details="true"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-row class="ma-0" justify="center" justify-md="start">
            <v-select
              style="max-width: 240px"
              class="input ma-0 pa-0"
              label="Status Type"
              v-model="selectedType"
              :items="statusTypes"
              rounded
              :hide-details="true"
              clearable
            ></v-select>
          </v-row>
        </v-col>
        <v-col cols="12" md="3">
          <v-row class="ma-0">
            <v-btn
              @click="enterCreateMode()"
              color="#e022d1"
              dark
              rounded
              width="140px"
              class="pink-btn"
            >Create Article</v-btn>
          </v-row>
        </v-col>
      </v-row>
      <v-row justify="center">
        <v-data-table
          style="width: 100%"
          :headers="headers"
          :items="filteredRequests"
          :items-per-page="10"
          class="elevation-1 mt-4 ml-4 mr-8"
        >
          <template v-slot:item.action="{ item }">
            <td>
              <v-icon color="#4390c0" @click="viewRequest(item)">mdi-eye</v-icon>
              <v-icon
                v-if="role == 'Super Admin'"
                color="#4390c0"
                @click="selectedRequest = item; deletingArticle = true"
              >mdi-delete</v-icon>
            </td>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip
              class="capatilize"
              :color="statusColor(item.status || 'pending')"
              dark
            >{{ item.status || "pending" }}</v-chip>
          </template>
        </v-data-table>
      </v-row>
    </div>
    <v-col
      v-if="creatingArticle"
      class="pa-6 d-flex flex-column fill-height"
      style="background-color: #f5f7fa"
    >
      <v-row align="center" class="mb-6">
        <v-icon
          size="32"
          color="#4390c0"
          class="mr-3 cursor-pointer"
          @click="handleBack"
        >mdi-arrow-left-circle</v-icon>
        <h2 class="text-h5 font-weight-medium">Create New Article</h2>
      </v-row>

      <v-card flat class="pa-6 rounded-xl elevation-2" color="white">
        <v-row class="mb-8" justify="center">
          <v-col cols="12" md="6" class="d-flex justify-center">
            <v-hover v-slot="{ isHovering, props }">
              <v-card
                v-bind="props"
                class="d-flex align-center justify-center position-relative cursor-pointer"
                :style="{
                  border: '2px dashed #cfd8dc',
                  backgroundColor: '#fafafa',
                  height: '250px',
                  width: '250px',
                  transition: '0.2s',
                  boxShadow: isHovering ? '0 0 0 2px #4390c0 inset' : 'none',
                }"
                rounded="xl"
                @click="triggerFileInput"
              >
                <template v-if="!previewURL">
                  <div class="text-center">
                    <v-icon size="48" color="#9e9e9e">mdi-image-plus</v-icon>
                    <div class="mt-2 text-subtitle-2 grey--text">Click to upload</div>
                  </div>
                </template>

                <template v-else>
                  <v-img :src="previewURL" class="rounded-xl" height="100%" width="100%" cover></v-img>

                  <div
                    class="absolute inset-0 d-flex align-center justify-center"
                    style="
                      background: rgba(0, 0, 0, 0.35);
                      opacity: 0;
                      transition: 0.2s;
                    "
                    @mouseover="$event.currentTarget.style.opacity = 1"
                    @mouseleave="$event.currentTarget.style.opacity = 0"
                  >
                    <v-icon color="white" size="36">mdi-upload</v-icon>
                  </div>
                </template>

                <input
                  ref="fileInput"
                  type="file"
                  accept="image/*"
                  style="display: none"
                  @change="handleFileChange"
                />
              </v-card>
            </v-hover>
          </v-col>
        </v-row>

        <v-row class="mb-3" justify="center">
          <v-col cols="12" md="8">
            <v-text-field
              v-model="articleTitle"
              label="Article Title"
              placeholder="Enter your article title"
              outlined
              clearable
              color="#4390c0"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row class="mb-6" justify="center">
          <v-col cols="12" md="8">
            <v-file-input
              v-model="articlePDF"
              label="Upload PDF"
              accept="application/pdf"
              prepend-inner-icon="mdi-file-pdf-box"
              outlined
              color="#4390c0"
              show-size
              clearable
            ></v-file-input>
          </v-col>
        </v-row>

        <v-row class="mb-3" justify="center">
          <v-col cols="12" md="8">
            <v-text-field
              v-model="articleURL"
              label="Article URL"
              placeholder="Enter a link to the article"
              outlined
              clearable
              color="#4390c0"
              :rules="[
                (v) => !v || /^https?:\/\/.+/.test(v) || 'Must be a valid URL',
              ]"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row justify="center" class="mt-8">
          <v-btn
            @click="createNewArticle()"
            color="#e022d1"
            rounded="xl"
            size="large"
            width="240"
            class="elevation-3 white--text"
          >Create Article</v-btn>
        </v-row>
      </v-card>
    </v-col>
    <div v-if="viewingRequest">
      <v-row justify="center">
        <v-row class="ma-2">
          <v-col cols="12" md="6">
            <v-row class="mb-2">
              <v-icon class="black-color" @click="close()">mdi-arrow-left</v-icon>
              <h4 class="grey-color mx-4" style="font-weight: 500">Request Info</h4>
            </v-row>
          </v-col>
          <v-col v-if="selectedRequest.reviewed || role == 'Admin'" cols="12" md="6">
            <v-row class="mb-2" justify="center" justify-md="end">
              <v-btn
                v-if="submittingArticle == false"
                @click="submittingArticle = true"
                dark
                rounded
                color="#4390c0"
              >Review</v-btn>
              <v-btn
                v-if="submittingArticle == true"
                @click="submittingArticle = false"
                dark
                rounded
                color="#4390c0"
              >Back to Article</v-btn>
            </v-row>
          </v-col>
        </v-row>
      </v-row>
      <v-row justify="center">
        <div class="white-card pa-8">
          <div v-if="!submittingArticle">
            <v-row justify="start" class="ma-0">
              <h2 class="fs400 mb-4">Article Info</h2>
              <v-chip
                class="status-chip mx-2 mt-1"
                style="text-transform: capitalize"
                :color="statusColor(selectedRequest.status || 'Pending')"
                dark
              >{{ selectedRequest.status || "Pending" }}</v-chip>
            </v-row>
            <v-row justify="center" class="ma-0">
              <v-col cols="12">
                <v-row justify="start">
                  <v-col cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Title:</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-text-field
                        readonly
                        filled
                        outlined
                        dense
                        rounded
                        v-model="selectedRequest.title"
                      ></v-text-field>
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Description:</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-text-field
                        readonly
                        filled
                        outlined
                        dense
                        rounded
                        v-model="selectedRequest.description"
                      ></v-text-field>
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Doctor:</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-text-field
                        readonly
                        filled
                        outlined
                        dense
                        rounded
                        v-model="selectedRequest.userName"
                      ></v-text-field>
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Created At:</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-text-field
                        readonly
                        filled
                        outlined
                        dense
                        rounded
                        v-model="selectedRequest.date"
                      ></v-text-field>
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>PDF:</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0 mb-4">
                      <a
                        v-if="selectedRequest.pdf"
                        :href="selectedRequest.pdf"
                        target="_blank"
                        rel="noopener"
                        class="text-primary"
                      >View PDF</a>
                      <p v-else>No PDF uploaded</p>
                    </v-row>
                  </v-col>
                </v-row>
                <v-row class="ma-0">
                  <p>
                    <strong>Image:</strong>
                  </p>
                </v-row>
                <v-row justify="center" justify-md="start" class="ma-0">
                  <img :src="selectedRequest.image" class="image mb-6" />
                </v-row>
              </v-col>
            </v-row>
          </div>
          <div v-if="submittingArticle">
            <v-row justify="start" class="ma-0">
              <h2 class="fs400 mb-4">Reviewing Article</h2>
            </v-row>
            <v-row v-if="selectedRequest.reviewedBy" justify="start" class="ma-0">
              <h4 class="fs400 mb-4">Reviewed by:</h4>
              <h4 class="blue-color mb-4">{{ selectedRequest.reviewedBy }}</h4>
            </v-row>

            <div style="max-height: 60vh; overflow-y: scroll">
              <v-form ref="reviewForm">
                <v-row justify="start" class="ma-0">
                  <h2 class="fs400 mb-2">1. Required Documentation</h2>
                </v-row>
                <v-row justify="start" class="ma-0">
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Completed Article Submission Form</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="articleReview.completedFormProvided"
                        required
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!articleReview.completedFormProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.completedFormProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="articleReview.completedFormProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.completedFormProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="articleReview.completedFormProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        class="mt-2"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="articleReview.completedFormProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Final Article (Word/Pdf Format)</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="articleReview.finalArticleProvided"
                        required
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!articleReview.finalArticleProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.finalArticleProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="articleReview.finalArticleProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.finalArticleProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="articleReview.finalArticleProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        class="mt-2"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="articleReview.finalArticleProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Practitioner headshot</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="articleReview.practitionerHeadshotProvided"
                        required
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!articleReview.practitionerHeadshotProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.practitionerHeadshotProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="articleReview.practitionerHeadshotProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.practitionerHeadshotProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="articleReview.practitionerHeadshotProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        class="mt-2"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="articleReview.practitionerHeadshotProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Short Professional Bio</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="articleReview.shortBioProvided"
                        required
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!articleReview.shortBioProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.shortBioProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="articleReview.shortBioProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.shortBioProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row v-show="articleReview.shortBioProvidedShow" justify="start" class="ma-0">
                      <v-textarea
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        class="mt-2"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="articleReview.shortBioProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>References/citations where applicable</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="articleReview.referencesProvided"
                        required
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!articleReview.referencesProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.referencesProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="articleReview.referencesProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.referencesProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="articleReview.referencesProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        class="mt-2"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="articleReview.referencesProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Proof of copyright or original authorship</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="articleReview.proofAuthorshipProvided"
                        required
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!articleReview.proofAuthorshipProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.proofAuthorshipProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="articleReview.proofAuthorshipProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.proofAuthorshipProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="articleReview.proofAuthorshipProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        class="mt-2"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="articleReview.proofAuthorshipProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                </v-row>

                <v-row justify="start" class="ma-0">
                  <h2 class="fs400 my-2">2. Content Compliance Checklist</h2>
                </v-row>
                <v-row justify="start" class="ma-0">
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>
                          Content is medically accurate and
                          evidence-based
                        </strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="articleReview.contentAccurateProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!articleReview.contentAccurateProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.contentAccurateProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="articleReview.contentAccurateProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.contentAccurateProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="articleReview.contentAccurateProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="articleReview.contentAccurateProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>
                          Author is verified as a qualified healthcare
                          practitioner
                        </strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="articleReview.authorVerifiedProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!articleReview.authorVerifiedProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.authorVerifiedProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="articleReview.authorVerifiedProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.authorVerifiedProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="articleReview.authorVerifiedProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="articleReview.authorVerifiedProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>No harmful, misleading, or unverified claims</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="articleReview.notHarmfulProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!articleReview.notHarmfulProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.notHarmfulProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="articleReview.notHarmfulProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.notHarmfulProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="articleReview.notHarmfulProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="articleReview.notHarmfulProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>
                          No promotion of unregistered medicines or devices
                          (SAPHRA aligned)
                        </strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="articleReview.noPromotionProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!articleReview.noPromotionProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.noPromotionProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="articleReview.noPromotionProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.noPromotionProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="articleReview.noPromotionProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="articleReview.noPromotionProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>No discrimination or unethical language</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="articleReview.noDiscriminationProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!articleReview.noDiscriminationProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.noDiscriminationProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="articleReview.noDiscriminationProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.noDiscriminationProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="articleReview.noDiscriminationProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="articleReview.noDiscriminationProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>
                          Content aligns with platform purpose and
                          guidelines
                        </strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="articleReview.contentAlignsProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!articleReview.contentAlignsProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.contentAlignsProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="articleReview.contentAlignsProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.contentAlignsProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="articleReview.contentAlignsProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="articleReview.contentAlignsProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Clear educational value to users</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="articleReview.clearValueProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!articleReview.clearValueProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.clearValueProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="articleReview.clearValueProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.clearValueProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="articleReview.clearValueProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="articleReview.clearValueProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>No copyright infringement</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="articleReview.noCopyrightProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!articleReview.noCopyrightProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.noCopyrightProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="articleReview.noCopyrightProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.noCopyrightProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="articleReview.noCopyrightProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="articleReview.noCopyrightProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>No conflict of interest undisclosed</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="articleReview.noConflictProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!articleReview.noConflictProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.noConflictProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="articleReview.noConflictProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.noConflictProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="articleReview.noConflictProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="articleReview.noConflictProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                </v-row>

                <v-row justify="start" class="ma-0">
                  <h2 class="fs400 my-2">3. Editorial Quality Checklist</h2>
                </v-row>
                <v-row justify="start" class="ma-0">
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Grammar and spelling checked</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="articleReview.spellingCheckedProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!articleReview.spellingCheckedProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.spellingCheckedProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="articleReview.spellingCheckedProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.spellingCheckedProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="articleReview.spellingCheckedProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="articleReview.spellingCheckedProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Clarity and readability acceptable</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="articleReview.clarityAcceptableProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!articleReview.clarityAcceptableProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.clarityAcceptableProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="articleReview.clarityAcceptableProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.clarityAcceptableProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="articleReview.clarityAcceptableProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="articleReview.clarityAcceptableProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Structure flows logically</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="articleReview.structureFlowsProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!articleReview.structureFlowsProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.structureFlowsProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="articleReview.structureFlowsProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.structureFlowsProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="articleReview.structureFlowsProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="articleReview.structureFlowsProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Word Count appropriate</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="articleReview.wordCountAppropriateProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!articleReview.wordCountAppropriateProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.wordCountAppropriateProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="articleReview.wordCountAppropriateProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.wordCountAppropriateProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="articleReview.wordCountAppropriateProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="articleReview.wordCountAppropriateProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Visuals/diagrams (if included) reviewed</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="articleReview.visualsReviewedProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!articleReview.visualsReviewedProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.visualsReviewedProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="articleReview.visualsReviewedProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          articleReview.visualsReviewedProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="articleReview.visualsReviewedProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="articleReview.visualsReviewedProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                </v-row>
                <v-row
                  v-if="role == 'Admin' && !selectedRequest.reviewed"
                  justify="start"
                  class="ma-0"
                >
                  <v-btn class="mt-4" @click="submitReview()" dark rounded color="#4390c0">Submit</v-btn>
                </v-row>
                <div v-if="role == 'Super Admin' && selectedRequest.reviewed">
                  <v-row justify="center" justify-md="start" class="ma-0">
                    <p>
                      <strong>Article Status:</strong>
                    </p>
                  </v-row>
                  <v-row justify="center" justify-md="start" class="ma-0">
                    <v-select
                      style="max-width: 240px"
                      class="ma-0 pa-0"
                      v-model="selectedRequest.status"
                      :items="reviewOptions"
                      filled
                      outlined
                      dense
                      rounded
                      :hide-details="true"
                    ></v-select>
                  </v-row>
                  <v-row
                    justify="center"
                    justify-md="start"
                    v-if="selectedRequest.status === 'denied'"
                    class="ma-0 mt-8"
                  >
                    <v-textarea
                      style="max-width: 240px"
                      v-model="selectedRequest.reason"
                      label="Reason for Denial"
                      auto-grow
                      outlined
                      filled
                      clearable
                      rounded
                      rows="2"
                      :rules="[(v) => !!v || 'Reason is required']"
                    ></v-textarea>
                  </v-row>
                  <v-row justify="center" justify-md="start" class="ma-0">
                    <v-btn
                      @click="updateStatus()"
                      color="#e022d1"
                      dark
                      rounded
                      width="240px"
                      class="pink-btn mt-4"
                    >Update Status</v-btn>
                  </v-row>
                </div>
              </v-form>
            </div>
          </div>
        </div>
      </v-row>
    </div>

    <v-row justify="center">
      <v-overlay :value="loading">
        <v-progress-circular :size="100" color="#4390c0" indeterminate />
      </v-overlay>
    </v-row>
    <v-dialog v-model="promptDialog" max-width="480">
      <v-card class="pa-8" elevation="4" shaped style="border-radius: 24px">
        <v-card-text class="text-center">
          <v-icon size="96" color="red" v-if="promptType == 'error'">mdi-alert-circle</v-icon>
          <v-icon size="96" color="green" v-if="promptType == 'success'">mdi-check-circle</v-icon>
          <div class="text-h5 my-6" v-if="promptType == 'error'">Error</div>
          <div class="text-h5 my-6" v-if="promptType == 'success'">Success</div>
          <div class="body-1 text-h6">{{ promptMessage }}</div>
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn
            color="black"
            dark
            depressed
            large
            style="border-radius: 24px"
            @click="promptDialog = false"
          >CLOSE</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="deletingArticle" max-width="480">
      <v-card class="pa-8" elevation="4" shaped style="border-radius: 24px">
        <v-card-text class="text-center">
          <v-icon size="96" color="green">mdi-check-circle</v-icon>
          <div class="text-h5 my-6">Delete Article</div>
          <div class="body-1 text-h6">Are you sure you want to delete this article?</div>
        </v-card-text>
        <v-row v-if="selectedRequest" justify="center" justify-md="center" class="ma-0">
          <v-textarea
            v-model="selectedRequest.deleteReason"
            label="Reason for deletion"
            auto-grow
            outlined
            filled
            clearable
            rounded
            rows="2"
            :rules="[(v) => !!v || 'Reason is required']"
            dense
          ></v-textarea>
        </v-row>
        <v-card-actions class="justify-center">
          <v-btn
            color="black"
            dark
            depressed
            large
            style="border-radius: 24px"
            @click="deletingArticle = false"
          >Cancel</v-btn>
          <v-btn
            color="#4390c0"
            dark
            depressed
            large
            style="border-radius: 24px"
            @click="deleteArticle()"
          >Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import {
  collection,
  getDocs,
  where,
  getFirestore,
  setDoc,
  query,
  getDoc,
  doc,
  addDoc,
  onSnapshot,
  deleteDoc
} from "firebase/firestore";
import { firebaseApp } from "../../firebaseConfig";
const db = getFirestore(firebaseApp);
import { logAction, sendPushNotificationUser } from "@/utils/firestoreUtil";
import {
  getStorage,
  ref,
  getDownloadURL,
  uploadBytes,
  deleteObject
} from "firebase/storage";

const storage = getStorage(firebaseApp);

export default {
  data() {
    return {
      articleURL: "",
      articleTitle: "",
      articlePDF: null,
      articleImage: null,
      previewURL: null,
      creatingArticle: false,
      headers: [
        { text: "Title", value: "title" },
        { text: "Created", value: "date" },
        { text: "Doctor", value: "doctorFullName" },
        { text: "Status", value: "status" },
        { text: "Action", value: "action" }
      ],
      search: "",
      statusTypes: ["pending", "reviewed", "approved", "denied"],
      selectedType: "pending",
      requests: [],
      selectedRequest: null,
      viewingRequest: false,
      loading: false,
      promptDialog: false,
      promptMessage: "",
      promptType: "",
      unsubscribeDoctors: null,
      unsubscribeArticles: null,
      submittingArticle: false,
      reviewOptions: ["approved", "denied"],
      articleReview: {},
      nullRules: [v => !!v || "Field is required"],
      doctors: [],
      deletingArticle: false
    };
  },
  methods: {
    handleBack() {
      console.log("Back button clicked");
      this.creatingArticle = false;
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    getDoctors() {
      try {
        this.loading = true;
        const q = query(collection(db, "doctors"));

        // Subscribe to real-time updates
        this.unsubscribeDoctors = onSnapshot(q, querySnapshot => {
          this.doctors = querySnapshot.docs.map(d => {
            const data = d.data();
            return {
              id: d.id,
              ...data,
              fullName: `${data.name} ${data.surname}`
            };
          });

          this.loading = false;
        });
      } catch (error) {
        console.log("Error: ", error);
        this.loading = false;
        this.promptMessage = "An error occurred while fetching the doctors!";
        this.promptType = "error";
        this.promptDialog = true;
      }
    },
    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.articleImage = file;
        this.previewURL = URL.createObjectURL(file);
      }
    },
    async createNewArticle() {
      if (!this.articleTitle) {
        this.promptMessage = "Please enter a title for the article!";
        this.promptType = "error";
        this.promptDialog = true;
        return;
      }

      this.loading = true;

      let imageURL = null;
      let imageRefPath = null;
      let pdfURL = null;
      let pdfRefPath = null;

      // Upload image if selected
      if (this.articleImage) {
        const imgRef = ref(
          storage,
          `Articles/${this.loggedInUser.id}/image_${Date.now()}.png`
        );
        await uploadBytes(imgRef, this.articleImage);
        imageURL = await getDownloadURL(imgRef);
        imageRefPath = imgRef.fullPath;
      }

      // Upload PDF if selected
      if (this.articlePDF) {
        const pdfRef = ref(
          storage,
          `Articles/${this.loggedInUser.id}/pdf_${Date.now()}.pdf`
        );
        await uploadBytes(pdfRef, this.articlePDF);
        pdfURL = await getDownloadURL(pdfRef);
        pdfRefPath = pdfRef.fullPath;
      }

      const newArticle = {
        title: this.articleTitle,
        dateCreated: new Date(),
        status: "pending",
        url: this.articleURL || null,
        imageRef: imageRefPath || null,
        pdfRef: pdfRefPath || null,
        lotusArticle: true
      };

      if (imageURL) {
        newArticle.imageUrl = imageURL;
        newArticle.imageRef = imageRefPath;
      }

      if (pdfURL) {
        newArticle.pdfUrl = pdfURL;
        newArticle.pdfRef = pdfRefPath;
      }

      try {
        await addDoc(collection(db, "articles"), newArticle);

        this.promptMessage = "Successfully created article!";
        this.promptType = "success";
        this.promptDialog = true;

        this.loading = false;

        setTimeout(() => {
          this.creatingArticle = false;
          this.getRequests();
        }, 1200);
      } catch (error) {
        console.log("Error creating article:", error);
        this.loading = false;

        this.promptMessage = "Something went wrong while creating the article!";
        this.promptType = "error";
        this.promptDialog = true;
      }
    },
    enterCreateMode() {
      this.creatingArticle = true;
      this.selectedRequest = null;
    },
    close() {
      this.viewingRequest = false;
      localStorage.removeItem("ViewedRequest");
    },
    viewRequest(item) {
      this.selectedRequest = item;
      this.viewingRequest = true;

      if (this.selectedRequest.reviewed) {
        this.articleReview = { ...this.selectedRequest };
      }

      localStorage.setItem(
        "ViewedRequest",
        JSON.stringify(this.selectedRequest)
      );
    },
    getRequests() {
      try {
        this.requests = [];
        this.loading = true;

        const q = query(collection(db, "articles"));

        this.unsubscribeArticles = onSnapshot(q, async querySnapshot => {
          const temp = [];

          for (const docSnap of querySnapshot.docs) {
            let article = docSnap.data();
            article.id = docSnap.id;

            // Format date
            if (article.createdAt) {
              article.date =
                new Date(article.createdAt.seconds * 1000)
                  .toISOString()
                  .substring(0, 10) +
                " " +
                new Date(article.createdAt.seconds * 1000).toLocaleTimeString();
            }

            // Attach doctor info correctly
            let doctor = this.doctors.find(d => d.id === article.userId);
            if (doctor) {
              article.doctor = doctor;
              article.doctorFullName = `${doctor.name} ${doctor.surname}`;
            } else {
              article.doctorFullName = "Unknown";
            }

            // Fetch image if exists
            if (article.imageRef) {
              try {
                article.imageUrl = await getDownloadURL(
                  ref(storage, article.imageRef)
                );
              } catch (err) {
                article.imageUrl = null;
              }
            }

            // Fetch PDF if exists
            if (article.pdfRef) {
              try {
                article.pdfUrl = await getDownloadURL(
                  ref(storage, article.pdfRef)
                );
              } catch (err) {
                article.pdfUrl = null;
              }
            }

            // Ensure status
            if (!article.status) article.status = "pending";

            temp.push(article);
          }

          this.requests = temp;
          this.loading = false;
        });
      } catch (error) {
        this.loading = false;
        this.promptMessage = "Failed to fetch articles.";
        this.promptType = "error";
        this.promptDialog = true;
      }
    },
    statusColor(status) {
      switch (status) {
        case "denied":
          return "#e022d1";
        case "pending":
          return "#4390c0";
        case "approved":
          return "green";
        default:
          return "grey";
      }
    },
    getDoctors() {
      try {
        this.loading = true;
        const q = query(collection(db, "doctors"));

        // Subscribe to real-time updates
        this.unsubscribeDoctors = onSnapshot(q, querySnapshot => {
          this.doctors = [];

          querySnapshot.forEach(doctor => {
            let tempDoctor = doctor.data();
            tempDoctor.id = doctor.id;

            this.doctors.push(tempDoctor);
          });

          this.loading = false;
        });
      } catch (error) {
        console.log("Error: ", error);
        this.loading = false;
        this.promptMessage = "An error occurred while fetching the doctors!";
        this.promptType = "error";
        this.promptDialog = true;
      }
    },
    async submitReview() {
      let valid = this.$refs.reviewForm.validate();

      if (!valid) {
        this.promptMessage = "Please fill out all required fields";
        this.promptType = "error";
        this.promptDialog = true;
        return;
      }

      this.selectedRequest.status = "reviewed";
      this.selectedRequest.reviewed = true;
      this.selectedRequest.reviewedBy =
        this.loggedInUser.name + " " + this.loggedInUser.surname;

      let newRequest = {
        ...this.selectedRequest,
        ...this.articleReview
      };

      try {
        this.loading = true;
        await setDoc(doc(db, "articles", this.selectedRequest.id), newRequest);
        this.loading = false;

        this.promptMessage = "Successfully submitted article review!";
        this.promptType = "success";
        this.promptDialog = true;
      } catch (error) {
        this.loading = false;
        console.log("Error: ", error);
        this.promptMessage =
          "Something went wrong while submitting your review, please try again!";
        this.promptType = "error";
        this.promptDialog = true;
      }
    },
    async updateStatus() {
      try {
        if (
          this.selectedRequest.status === "denied" &&
          !this.selectedRequest.reason
        ) {
          this.promptMessage = "Please provide a reason for denial.";
          this.promptType = "error";
          this.promptDialog = true;
          return;
        }

        this.loading = true;
        await setDoc(doc(db, "articles", this.selectedRequest.id), {
          ...this.selectedRequest,
          reason:
            this.selectedRequest.status === "denied"
              ? this.selectedRequest.reason
              : ""
        });

        this.loading = false;

        if (this.selectedRequest.status == "denied") {
          if (this.selectedRequest.doctor.fcmToken) {
            let res = await sendPushNotificationUser(
              "Ad Denied",
              "Your ad for " +
                this.selectedRequest.title +
                " has been declined for the following reason: " +
                this.selectedRequest.reason,
              this.selectedRequest.doctor.fcmToken
            );
          }
          this.addNotification(
            this.selectedRequest.userId,
            "Article Denied",
            "Your article: " +
              this.selectedRequest.title +
              " has been declined for the following reason: " +
              this.selectedRequest.reason
          );
        } else if (this.selectedRequest.status == "approved") {
          if (this.selectedRequest.doctor.fcmToken) {
            let res = await sendPushNotificationUser(
              "Ad Approved",
              "Your ad for " +
                this.selectedRequest.title +
                " has been approved",
              this.selectedRequest.doctor.fcmToken
            );
          }
          this.addNotification(
            this.selectedRequest.userId,
            "Article Approved",
            "Your article: " + this.selectedRequest.title + " has been approved"
          );
        }

        await this.logStatusUpdate();

        this.promptMessage = "Successfully updated article status!";
        this.promptType = "success";
        this.promptDialog = true;

        setTimeout(() => {
          this.promptDialog = false;
        }, 3000);
      } catch (error) {
        this.loading = false;
        console.log("error: ", error);
        this.promptMessage =
          "An error occured while updating the article's status, please try again!";
        this.promptType = "error";
        this.promptDialog = true;
      }
    },
    async logStatusUpdate() {
      await logAction(
        "Ad Management",
        "Status Update",
        this.loggedInUser.email,
        `${this.loggedInUser.email} has updated the status of ad ${
          this.selectedRequest.id
        } to ${this.selectedRequest.status}. Reason: ${this.selectedRequest
          .reason || "N/A"}`
      );
    },
    async addNotification(userID, type, message) {
      let notification = {
        createdAt: new Date(),
        userType: "Doctor",
        userID: userID,
        type: type,
        message: message
      };

      try {
        this.loading = true;
        let res2 = await addDoc(collection(db, "alerts"), notification);
        this.loading = false;
      } catch (error) {
        console.log("Error: ", error);
        this.loading = false;
      }
    },
    async deleteArticle() {
      if (
        !this.selectedRequest.deleteReason ||
        (this.selectedRequest.deleteReason &&
          this.selectedRequest.deleteReason.length < 4)
      ) {
        this.promptMessage = "Please provide a reason for deletion!";
        this.promptType = "error";
        this.promptDialog = true;
        return;
      }

      this.deletingArticle = false;

      try {
        this.loading = true;

        let reason = this.selectedRequest.deleteReason;

        if (this.selectedRequest.pdfRef) {
          let newRef = ref(storage, this.selectedRequest.pdfRef);
          await deleteObject(newRef);
        }

        await deleteDoc(doc(db, "articles", this.selectedRequest.id));
        this.loading = false;

        if (this.selectedRequest.doctor?.fcmToken) {
          await sendPushNotificationUser(
            "Adrticle Deleted",
            `Your article for ${this.selectedRequest.title} has been deleted for the following reason: ${reason}`,
            this.selectedRequest.doctor.fcmToken
          );
        }

        this.addNotification(
          this.selectedRequest.userId,
          "Article Deleted",
          "Your article for " +
            this.selectedRequest.title +
            " has been deleted for the following reason: " +
            reason
        );

        this.promptMessage = "Successfully deleted article!";
        this.promptType = "success";
        this.promptDialog = true;
      } catch (error) {
        this.loading = false;
        console.log("Storage Delete Doc Error: ", error);
        this.promptMessage =
          "Something went wrong while deleting the article, please try again!";
        this.promptType = "error";
        this.promptDialog = true;
      }
    }
  },
  async created() {
    await this.getDoctors();
    await this.getRequests();

    if (localStorage.getItem("ViewedRequest")) {
      let selectedRequest = JSON.parse(localStorage.getItem("ViewedRequest"));
      let newRequest = this.requests.find(
        request => request.id == selectedRequest.id
      );

      if (newRequest) {
        this.viewRequest(newRequest);
      }
    }
  },
  computed: {
    filteredRequests() {
      return this.requests.filter(item => {
        const matchesStatus =
          this.selectedType === null || item.status === this.selectedType;

        const matchesSearch = Object.entries(item).some(([key, value]) => {
          if (value && typeof value !== "object") {
            return value
              .toString()
              .toLowerCase()
              .includes(this.search.toLowerCase());
          }
          return false;
        });

        return matchesStatus && matchesSearch;
      });
    },
    loggedInUser() {
      return this.$store.state.user || {};
    },
    role() {
      return this.loggedInUser.adminType;
    }
  },
  beforeUnmount() {
    if (this.unsubscribeArticles) {
      this.unsubscribeArticles();
    }

    if (this.unsubscribeDoctors) {
      this.unsubscribeDoctors();
    }
  }
};
</script>

<style scoped>
.reason-field {
  background-color: #fafafa;
  border-radius: 12px;
}

.image {
  width: 200px;
  border: 2px solid grey;
}
</style>
